import { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export function Modal({ isOpen, onClose, title, description, children, size = 'md' }: ModalProps) {
  useEffect(() => {
    // Only do work when actually open. Cleanup fires when isOpen flips
    // back to false OR the modal unmounts mid-open — both are the
    // moments where the iOS keyboard race below matters.
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => {
      // Use '' (remove inline style) rather than 'unset' (CSS keyword
      // that means "inherit or initial"); the inline 'unset' has been
      // observed to stick on iOS Safari and prevent the body from
      // recomputing its layout against the now-restored viewport.
      document.body.style.overflow = '';
      if (typeof document === 'undefined' || typeof window === 'undefined') return;
      // iOS-Safari layout race on modal close (the "Add Building →
      // Cancel leaves a gray gap at the bottom" QA repro): the input
      // inside the modal is still focused at the moment of close, so
      // the keyboard collapses *after* the layout has snapshotted
      // its keyboard-open viewport size. Even with
      // interactive-widget=resizes-content + a synthetic resize, iOS
      // sometimes keeps cached heights in the flex/overflow chain
      // (h-dvh root → <main overflow-y-auto>) and the bottom of the
      // page never reflows.
      //
      // Three layers of defense, fired after iOS has had a beat to
      // close the keyboard:
      //   1. Blur the active element so the keyboard collapse is
      //      already in flight before the cleanup work.
      //   2. Toggle the documentElement's min-height by a fractional
      //      pixel and force a synchronous reflow (offsetHeight read).
      //      This invalidates any cached layout heights that iOS held
      //      onto from the keyboard-open viewport.
      //   3. Dispatch a synthetic resize so any listeners that key
      //      off viewport size also recompute.
      (document.activeElement as HTMLElement | null)?.blur?.();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const html = document.documentElement;
          const prevMinHeight = html.style.minHeight;
          // Force the layout to break and recompute. The exact value
          // doesn't matter as long as it's slightly different from
          // the natural one — `100.001%` lands within rounding error
          // of the document's existing height.
          html.style.minHeight = '100.001%';
          // Read offsetHeight to flush layout synchronously.
          void html.offsetHeight;
          html.style.minHeight = prevMinHeight;
          window.dispatchEvent(new Event('resize'));
        });
      });
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={cn(
          'relative w-full bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-h-[calc(100dvh-2rem)] flex flex-col',
          sizes[size],
        )}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
            {description && (
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{description}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto p-6 flex-1 min-h-0">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
